import React, { useEffect, useState } from 'react';
import BaseModal from '../../components/modals/BaseModal';
import getPasswordStrength from '../../utils/passwordStrength';
import { useNavigate, useParams } from 'react-router-dom';
import FormGroup from '../../components/controls/Form/components/FormGroup';
import From from '../../components/controls/Form';
import InputField from '../../components/controls/Form/components/InputField';
import PasswordStrength from '../../components/controls/PasswordStrength';
import Button from '../../components/controls/Button';
import { apiPOST } from '../../utils/networking';
import Heading from '../../components/controls/Typography/Heading';

import './ChangePassword.less';

export const ChangePassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [revealPassword, setRevealPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [revealConfirmPassword, setRevealConfirmPassword] = useState(false);

  const handleFormSubmit = async () => {
    setIsBusy(true);
    try {
      setErrorMessage('');
      await apiPOST('/recommendation/rest/profile/changePassword', { token, password });
      setOpenSuccessModal(true);
    } catch (error) {
      console.error(error);
      setErrorMessage('The password reset token has expired, please return to the forgot password page to request a new one.');
    } finally {
      setIsBusy(false);
    }
  }

  const proceedToLogIn = () => {
    setOpenSuccessModal(false);
    navigate('/login');
  };

  useEffect(() => {
    document.title = 'Letters Portal - Reset Password';
  }, [])

  return (
    <main className="lsn-page lsn-page-change-password container">

      <Heading
        className="no-focus-outline text-center"
        tabIndex={0}
        aria-label={`Letters portal by Liaison Reset Password`}
      >
        Reset Password
      </Heading>

      <From
        isMedium
        hasBorder
        isButtonsStack
        noAutocomplete
        onSubmit={handleFormSubmit}
        formAttr={{
          title: 'Reset Password',
        }}
        buttons={
          <>
            <Button
              isBusy={isBusy}
              primary
              submit
            >
              Save and Continue
            </Button>

            <Button
              primary
              ghost
              disabled={isBusy}
              onClick={proceedToLogIn}
              type='button'
            >
              Cancel and Return to Sign In
            </Button>
          </>
        }
      >

        {errorMessage ? <div
          className="lsn-input-error text-center color-red mb-2 pb-2"
          role={`alert`}
          aria-label={`Alert ` + errorMessage}
        >
          {errorMessage}
        </div> : void 0}

        <FormGroup
          label={'Password'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          classNameControlWrapper={'show-password-input-container'}
          controlRender={({ id }) => <InputField
            title={'Password'}
            name={'password'}
            id={id}
            inputType={revealPassword ? 'text' : 'password'}
            autoComplete={'new-password'}
            onChange={(n, value) => setPassword(value)}
            value={password}
            required
            customValidator={({ value, errorMessage }) => {
              const passwordStrength = getPasswordStrength(value);
              let errMsg = '';
              if (!value) {
                errMsg = errorMessage?.toString() ?? '';
              } else if (passwordStrength.possibleMatch === 'poor') {
                errMsg = 'Your password must be a minimum of 8 characters.';
              } else if (passwordStrength.possibleMatch === 'weak') {
                errMsg = `Your password must meet the requirements.`;
              }
              return errMsg || errorMessage;
            }}
            postControlSlot={<>
              <span
                className="show-password-btn"
                tabIndex={0}
                role="button"
                aria-label={revealConfirmPassword ? `Hide password clickable` : `Clickable View password`}
                onKeyPress={() => setRevealPassword(!revealPassword)}
                onClick={() => setRevealPassword(!revealPassword)}
              >
                {revealPassword ? <i className="fa fa-eye-slash"></i> : <i className="fa fa-eye"></i>}
              </span>
              <PasswordStrength value={password} />
            </>}
          />}
        />

        <FormGroup
          label={'Confirm Password'}
          required
          horizontal
          classNameControlWrapper={'show-password-input-container'}
          labelSize={4}
          controlRender={({ id }) => <InputField
            title={'Confirm Password'}
            name={'confirmPassword'}
            id={id}
            inputType={revealConfirmPassword ? 'text' : 'password'}
            autoComplete={'new-password'}
            onChange={(n, value) => setConfirmPassword(value)}
            value={confirmPassword}
            placeholder={'Password'}
            required
            {
            ...(password && confirmPassword && password !== confirmPassword
              ? { customErrorMessage: 'Passwords must match', } : {})
            }
            postControlSlot={<>
              <span
                className="show-password-btn"
                tabIndex={0}
                role="button"
                aria-label={revealConfirmPassword ? `Hide password clickable` : `Clickable View password`}
                onKeyPress={() => setRevealConfirmPassword(!revealConfirmPassword)}
                onClick={() => setRevealConfirmPassword(!revealConfirmPassword)}
              >
                {revealConfirmPassword ? <i className="fa fa-eye-slash"></i> : <i className="fa fa-eye"></i>}
              </span>
            </>}
          />}
        />

      </From>

      <BaseModal
        size='small'
        show={openSuccessModal}
        title={'Password Changed'}
        modalBodyClass={'change-password-success-modal-body'}
        body={<>
          <i className='far fa-check-circle color-dark-green fa-3x'></i>
          You have successfully changed your account password. Please log in to access your account.
        </>
        }
        modalPrimaryButton={'Log In'}
        onClose={proceedToLogIn}
      ></BaseModal>
    </main>
  );

}

export default ChangePassword;
